// src/router/tenantRouting.ts
import type { Router, RouteLocationRaw } from 'vue-router';
import { useTenantStore } from '@/stores/useTenantStore';
import { useMedicalRequestStore } from '@/stores/useMedicalRequestStore';
import { useAdminRequestStore } from '@/stores/useAdminRequestStore';
import { useRepeatPrescriptionRequestStore } from '@/stores/useRepeatPrescriptionRequestStore';
import { NormalisedErrorItem, useErrorStore } from '@/stores/useErrorStore';
import { useNhsAppStore } from '@/stores/useNhsAppStore';

const TENANT_REGEX = /^[A-Fa-f0-9]{6}$/;

// ** IMPORTANT **
// we modify all .push() calls to add the tenant prefix if needed.
// we modify all router-link components to add the tenant prefix if needed.

export function withTenantPath(path: string): string {
  if (!path.startsWith('/')) {
    path = '/' + path;
  }

  const segments = path.split('/').filter(Boolean);
  const first = segments[0] ?? '';

  // already tenant-prefixed
  if (TENANT_REGEX.test(first)) {
    return path;
  }

  const tenantStore = useTenantStore();
  const tenantId = tenantStore.tenant?.tenantId;

  if (!tenantId) {
    return path;
  }

  return '/' + [tenantId, ...segments].join('/');
}

export function patchTenantAwarePush(router: Router) {
  const originalPush = router.push.bind(router);

  router.push = function patchedPush(location: RouteLocationRaw) {
    let path: string | null = null;

    if (typeof location === 'string') {
      path = location;
    } else if ('path' in location && location.path) {
      path = location.path;
    } else {
      return originalPush(location);
    }

    const newPath = withTenantPath(path);

    const newLocation: RouteLocationRaw =
      typeof location === 'string' ? newPath : { ...location, path: newPath };

    return originalPush(newLocation);
  } as typeof router.push;
}

/**
 * Register the beforeEach guards that depend on tenant + stores.
 */
export function registerTenantGuards(router: Router) {
  router.beforeEach(async (to, from) => {
    const title = to.meta.pageTitle || to.meta.layoutPageTitle || '';
    document.title = `Patient Portal ${title ? `| ${title}` : ''}`;

    const medicalRequestStore = useMedicalRequestStore();
    const adminRequestStore = useAdminRequestStore();
    const repeatPrescriptionRequestStore = useRepeatPrescriptionRequestStore();
    const tenantStore = useTenantStore();
    const errorStore = useErrorStore();
    const nhsAppStore = useNhsAppStore();

    const segments = to.path.split('/').filter(Boolean);
    const tenantId = segments[0] ?? null;
    const firstUrlSegment = segments[1] ?? null;

    let store:
      | ReturnType<typeof useMedicalRequestStore>
      | ReturnType<typeof useAdminRequestStore>
      | ReturnType<typeof useRepeatPrescriptionRequestStore>
      | null = null;

    if (firstUrlSegment === 'medical-request') store = medicalRequestStore;
    else if (firstUrlSegment === 'admin-request') store = adminRequestStore;
    else if (firstUrlSegment === 'repeat-prescription-request')
      store = repeatPrescriptionRequestStore;

    // NB from is null on initial load '
    const isInitialLoad = !from.name;

    if (to.meta.requiresLoadedTenant && !tenantStore.isLoaded) {
      console.warn('route requiresLoadedTenant', tenantId);
      await tenantStore.fetchTenant(tenantId);

      if (!tenantStore.isLoaded) {
        return tenantId ? `/${tenantId}` : '/';
      }
    }

    if (to.query['nhs-app-session'] && isInitialLoad) {
      // NB sets isNhsWebApp if user refreshes page.
      console.warn(
        'route requiresValidPatient, nhs-app-session, isInitialLoad',
        true,
        isInitialLoad,
      );
      nhsAppStore.setIsNhsWebApp(true);
    }

    if (to.meta.requiresValidPatient && !store?.isValidPatient) {
      console.warn(
        'route requiresValidPatient, isValidPatient : false',
        to.query['nhs-app-session'],
        isInitialLoad,
      );

      if (to.query['nhs-app-session'] && isInitialLoad) {
        console.warn(
          'route requiresValidPatient, nhs-app-session, isInitialLoad',
          true,
          isInitialLoad,
        );
        nhsAppStore.setIsNhsWebApp(true);
        const error: NormalisedErrorItem = {
          code: 'browser-action-issue',
        };
        errorStore?.setErrors([error]);
        return tenantId ? `/${tenantId}/error/nhs` : '/error';
      }

      return tenantId ? `/${tenantId}` : '/';
    }

    if (to.meta.redirectIfVerified && store?.isVerified) {
      return tenantId
        ? `/${tenantId}/repeat-prescription-request/patient-details`
        : '/';
    }

    if (to.meta.requiresVerifiedPatient && !store?.isVerified) {
      console.error('route requiresVerifiedPatient', false);
      return tenantId ? `/${tenantId}` : '/';
    }

    return true;
  });
}
